import { FC, useEffect, useRef, useState } from "react";
import wrapperHOC from "@Utils/wrapperHOC";

import styles from "./AmberHomeMark.module.scss";

/**
 * The footer logo, and its easter egg: MOVING DAY.
 *
 * Headout's footer mark tells its own name — hover it and the letters are pulled one
 * by one into a portal: the word literally heads out. This is amber's version of that
 * idea, and amber's story is a student getting a home:
 *
 *   1. a little line-drawn house builds itself just past the "r", and its door opens;
 *   2. the letters move in — r, e, b, m, a — each hopping to the door on its own arc,
 *      like someone carrying a suitcase, with a puff of dust as it leaves the ground;
 *   3. the door shuts, the house gives a happy bounce and the window lights up amber
 *      (the pun is the point) — everyone's home;
 *   4. the home becomes the moving van: it drives left, leaning into the trip with
 *      speed lines behind it, onto the spot where the word stood;
 *   5. the door opens and everyone unpacks — a first — each hopping right into place,
 *      so the logo ends as HOUSE + "amber", the home now part of the name; the door
 *      shuts, a heart pops over the roof, the chimney keeps smoking;
 *   6. and then the house quietly bows out — it fades away while the word glides back
 *      to its own spot, so the story ends on the logo exactly as it started;
 *   7. leave mid-story and it resets instead: the house fades, the letters settle back.
 *
 * The wordmark is amber-hd.svg split into one path per letter (each letter keeps its
 * own counters, so the holes in a/b/e still cut out). At rest the five paths render
 * exactly the original logo, and the house lives outside its 101×40 box with
 * `overflow: visible`, so the logo keeps its footprint.
 *
 * It is a three-phase state machine rather than a CSS `:hover`: with `:hover` alone,
 * leaving mid-story would SNAP everything home. `out` is a short reset that reads the
 * same from any point in the story, and re-entering starts moving day again.
 */

/**
 * `dx`: how far each letter travels to reach the door where the house is built (x=120).
 * `ex`: where the same door is once the house has driven left (x=17), relative to the
 * letter's own spot — the point it steps back out from.
 */
const LETTERS = [
  {
    key: "a",
    dx: 111.2,
    cx: 8.8,
    ex: 8.2,
    d: "M 13.333,14.565 L 17.6,14.565 L 17.6,31.57 L 13.333000000000002,31.57 L 13.333000000000002,29.773 Q 12.083000000000002,30.961 10.827000000000002,31.483999999999998 A 7.0,7.0 0.0 0,1 8.103000000000002,32.007999999999996 Q 4.809000000000001,32.007999999999996 2.4040000000000017,29.451999999999995 Q -0.0009999999999976694,26.895999999999994 0.0,23.098 Q 0.0,19.157999999999998 2.326,16.643 Q 4.652,14.126000000000001 7.978,14.127 A 7.2,7.2 0.0 0,1 10.85,14.705 Q 12.193,15.284 13.332,16.44 L 13.333,14.565 M 8.847999999999999,18.066 Q 6.868999999999999,18.066 5.560999999999999,19.465 Q 4.252999999999999,20.864 4.252999999999999,23.052 Q 4.252999999999999,25.256 5.584999999999999,26.678 Q 6.916999999999999,28.1 8.864,28.1 Q 10.874,28.1 12.198,26.701 Q 13.522,25.303 13.522,23.036 Q 13.522,20.816000000000003 12.198,19.441000000000003 Q 10.875,18.065 8.849,18.065 L 8.847999999999999,18.066",
  },
  {
    key: "m",
    dx: 86.37,
    cx: 33.63,
    ex: -16.63,
    d: "M 20.6,15.002 L 24.867,15.002 L 24.867,16.956 Q 25.962,15.753 27.299,15.158999999999999 Q 28.636,14.564999999999998 30.216,14.565 Q 31.811,14.565 33.094,15.344999999999999 Q 34.376000000000005,16.128 35.159,17.628 Q 36.176,16.128 37.653999999999996,15.346 Q 39.132,14.565 40.88399999999999,14.565 Q 42.69799999999999,14.564 44.07399999999999,15.408999999999999 Q 45.45199999999999,16.253 46.05399999999999,17.613 Q 46.65499999999999,18.973 46.655999999999985,22.036 L 46.655999999999985,32.008 L 42.35799999999998,32.008 L 42.35799999999998,23.38 Q 42.35799999999998,20.488 41.637999999999984,19.464 Q 40.920999999999985,18.441 39.481999999999985,18.439999999999998 Q 38.387999999999984,18.439999999999998 37.51999999999998,19.064999999999998 Q 36.65199999999998,19.689999999999998 36.22999999999998,20.791999999999998 Q 35.807999999999986,21.894 35.80899999999998,24.331999999999997 L 35.80899999999998,32.006 L 31.51,32.006 L 31.51,23.769 Q 31.51,21.487 31.173000000000002,20.462999999999997 Q 30.836000000000002,19.439999999999998 30.162000000000003,18.938999999999997 Q 29.488000000000003,18.438999999999997 28.532000000000004,18.438999999999997 Q 27.468000000000004,18.438999999999997 26.598000000000003,19.071999999999996 Q 25.728,19.705999999999996 25.298000000000002,20.845999999999997 Q 24.868000000000002,21.985999999999997 24.867,24.440999999999995 L 24.867,32.00599999999999 L 20.6,32.00599999999999 L 20.6,15.002",
  },
  {
    key: "b",
    dx: 61.54,
    cx: 58.46,
    ex: -41.46,
    d: "M 53.892,8.0 L 53.892,16.439999999999998 Q 55.04900000000001,15.283999999999997 56.393,14.704999999999998 A 7.2,7.2 0.0 0,1 59.269,14.126999999999999 Q 62.599,14.126999999999999 64.92699999999999,16.643 Q 67.255,19.159000000000002 67.25699999999999,23.098 Q 67.25599999999999,26.896 64.84899999999999,29.451999999999998 Q 62.44199999999999,32.007999999999996 59.14399999999999,32.007999999999996 Q 57.67499999999999,32.007999999999996 56.40899999999999,31.483999999999995 Q 55.142999999999994,30.960999999999995 53.89199999999999,29.772999999999996 L 53.89199999999999,31.569999999999997 L 49.65599999999999,31.569999999999997 L 49.65599999999999,8.0 L 53.892,8.0 M 58.378,18.066000000000003 Q 56.362,18.066000000000003 55.033,19.442000000000004 Q 53.704,20.818000000000005 53.705,23.037000000000003 Q 53.705,25.304000000000002 55.033,26.702 Q 56.362,28.102 58.378,28.101000000000003 Q 60.332,28.101000000000003 61.668,26.679000000000002 Q 63.004,25.257 63.004,23.053 Q 63.004,20.865000000000002 61.690999999999995,19.466 Q 60.37799999999999,18.068 58.376999999999995,18.067 L 58.378,18.066000000000003",
  },
  {
    key: "e",
    dx: 40.81,
    cx: 79.19,
    ex: -62.19,
    d: "M 88.09,24.302 L 74.384,24.302 Q 74.681,26.115 75.97,27.186 Q 77.259,28.257 79.26,28.256 Q 81.65100000000001,28.257 83.37,26.584 L 86.96600000000001,28.272 Q 85.62200000000001,30.177999999999997 83.74600000000001,31.092 Q 81.87100000000001,32.007 79.29100000000001,32.007999999999996 Q 75.28900000000002,32.007999999999996 72.77300000000001,29.483999999999995 Q 70.257,26.959999999999994 70.257,23.161999999999995 Q 70.257,19.268999999999995 72.766,16.698999999999995 Q 75.275,14.128999999999994 79.05600000000001,14.127999999999995 Q 83.07500000000002,14.127999999999995 85.59000000000002,16.697999999999993 Q 88.10700000000001,19.270999999999994 88.10700000000001,23.489999999999995 L 88.09,24.302 M 83.824,20.942 Q 83.401,19.519 82.15599999999999,18.629 Q 80.91099999999999,17.739000000000004 79.26599999999999,17.738 Q 77.481,17.738 76.136,18.738 Q 75.28999999999999,19.364 74.57,20.942 L 83.823,20.942",
  },
  {
    key: "r",
    dx: 24.33,
    cx: 95.67,
    ex: -78.67,
    d: "M 91.107,15.002 L 94.764,15.002 L 94.764,17.142 Q 95.35799999999999,15.876999999999999 96.344,15.221 A 3.8,3.8 0.0 0,1 98.5,14.565 Q 99.328,14.565 100.235,15.003 L 98.907,18.676000000000002 Q 98.157,18.301000000000002 97.672,18.301000000000002 Q 96.688,18.301000000000002 96.00699999999999,19.520000000000003 Q 95.32699999999998,20.740000000000002 95.32699999999998,24.303000000000004 L 95.34199999999998,25.131000000000004 L 95.34199999999998,32.008 L 91.10599999999998,32.008 L 91.107,15.002",
  },
];

/** Move-in order: the letter nearest the door goes first. */
const MOVE_IN_ORDER = ["r", "e", "b", "m", "a"];

/**
 * "done" is the story's own ending: the house has faded and the word is home, which
 * renders identically to "idle" — so leaving from "done" needs no reset animation.
 */
type Phase = "idle" | "in" | "done" | "out";

/** When `.movingIn` finishes — the word is back in place (see the SCSS timeline). */
const STORY_MS = 6700;

/** How long the reset takes before the mark is back at rest. */
const MOVE_OUT_MS = 520;

const AmberHomeMark: FC = () => {
  const [phase, setPhase] = useState<Phase>("idle");
  const timer = useRef(0);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const enter = () => {
    window.clearTimeout(timer.current);
    setPhase("in");
    timer.current = window.setTimeout(() => setPhase("done"), STORY_MS);
  };

  const leave = () => {
    window.clearTimeout(timer.current);
    // Only a story cut short needs the reset; a finished one already looks like rest.
    setPhase((p) => (p === "in" ? "out" : "idle"));
    timer.current = window.setTimeout(() => setPhase("idle"), MOVE_OUT_MS);
  };

  const phaseClass = { idle: "", done: "", in: styles.movingIn, out: styles.movingOut }[phase];

  return (
    <span className={`${styles.wrap} ${phaseClass}`} onMouseEnter={enter} onMouseLeave={leave}>
      <svg
        className={styles.mark}
        viewBox="0 0 101 40"
        width="96"
        height="38"
        role="img"
        aria-label="amber"
      >
        {/* the letters (the residents) */}
        {LETTERS.map((l) => (
          <path
            key={l.key}
            className={styles.letter}
            d={l.d}
            fill="#000"
            style={
              {
                "--dx": `${l.dx}px`,
                "--ex": `${l.ex}px`,
                "--order": MOVE_IN_ORDER.indexOf(l.key),
                // Unpacking runs the other way: "a" steps out first, nearest the house.
                "--out-order": LETTERS.indexOf(l),
              } as React.CSSProperties
            }
          />
        ))}

        {/* dust kicked up as each letter takes off */}
        {LETTERS.map((l) => (
          <g
            key={`dust-${l.key}`}
            className={styles.dust}
            style={{ "--order": MOVE_IN_ORDER.indexOf(l.key) } as React.CSSProperties}
          >
            <circle cx={l.cx - 4} cy="33" r="1.1" />
            <circle cx={l.cx + 4} cy="33.4" r="0.8" />
          </g>
        ))}

        {/* the home */}
        <g className={styles.house}>
          <g
            fill="none"
            stroke="#111827"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path
              className={`${styles.draw} ${styles.drawRoof}`}
              pathLength={1}
              d="M105 19.5 120 6.5l15 13"
            />
            <path
              className={`${styles.draw} ${styles.drawChimney}`}
              pathLength={1}
              d="M127.5 13V8.5h3v8"
            />
            <path
              className={`${styles.draw} ${styles.drawWalls}`}
              pathLength={1}
              d="M108.5 17v15h23V17"
            />
            <path className={`${styles.draw} ${styles.drawFloor}`} pathLength={1} d="M103 32h34" />
          </g>
          {/* the doorway, and the door that swings on its left hinge */}
          <rect className={styles.doorway} x="116" y="22.5" width="8" height="9.5" rx="1" />
          <rect className={styles.door} x="116" y="22.5" width="8" height="9.5" rx="1" />
          <rect className={styles.window} x="125.5" y="21.5" width="4" height="4" rx="0.8" />
          <circle className={`${styles.smoke} ${styles.smoke1}`} cx="129" cy="6" r="1.2" />
          <circle className={`${styles.smoke} ${styles.smoke2}`} cx="130.5" cy="3.5" r="0.9" />
          {/* speed lines, trailing the house as it drives left */}
          <g className={styles.speed} stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round">
            <path d="M139 12h7" />
            <path d="M140 20h9" />
            <path d="M139 27h6" />
          </g>
          <path
            className={styles.heart}
            d="M120 4.2c-.9-1.6-3.4-1.4-3.4.6 0 1.3 1.6 2.4 3.4 3.8 1.8-1.4 3.4-2.5 3.4-3.8 0-2-2.5-2.2-3.4-.6Z"
          />
        </g>
      </svg>
    </span>
  );
};

export default wrapperHOC(AmberHomeMark, {
  componentName: "AmberHomeMark-HomeV2Footer",
  showForChina: true,
});
